/**
 * BB ID／パスワード一覧(2026-09-09 ②指示)。スタッフ個人のBlueBeanログイン情報。
 * admin のみ(ルールでも bb_credentials は admin 限定)。パスワードは伏せ字で表示し、行ごとに「表示」で確認できる。
 * 監査ログにはパスワードの内容を残さない。
 */
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import Modal from '@core/components/Modal';
import MultiSelect, { matchesFilter } from '@core/components/MultiSelect';
import { SearchBox, SortTh, matchKeyword, sortRows, useSort } from '@core/components/tableTools';
import { useToast } from '@core/components/Toast';
import { useCurrentUser } from '@core/auth/AuthProvider';
import { fmtTs } from '@core/lib/auditLog';
import {
  BUSINESS_LABELS,
  OPERATOR_STATUSES,
  OPERATOR_STATUS_LABELS,
  PAY_TYPE_LABELS,
  WORK_STYLE_LABELS,
  type BbAccount,
  type BbCredential,
  type Operator,
  type OperatorStatus,
  type PayType,
  type WorkStyle,
} from '@core/lib/types';
import { fetchOperators } from './operatorRepo';
import { deleteBbCredential, fetchBbCredentials, saveBbCredential } from './bbCredentialRepo';

type Row = { operator: Operator; account: BbAccount; credential: BbCredential | null };
type SortKey = 'bb_id' | 'ext' | 'name' | 'work_style' | 'pay_type' | 'business' | 'status' | 'updated_at';

const STATUS_OPTIONS = OPERATOR_STATUSES.map((key) => ({ key, label: OPERATOR_STATUS_LABELS[key] }));
const WORK_STYLE_OPTIONS = (Object.keys(WORK_STYLE_LABELS) as WorkStyle[]).map((key) => ({ key, label: WORK_STYLE_LABELS[key] }));
const PAY_TYPE_OPTIONS = (Object.keys(PAY_TYPE_LABELS) as PayType[]).map((key) => ({ key, label: PAY_TYPE_LABELS[key] }));

const tsMillis = (ts: unknown): number =>
  ts && typeof (ts as { toMillis?: () => number }).toMillis === 'function' ? (ts as { toMillis: () => number }).toMillis() : 0;

/** 内線は数字なので数値で並べる(数字でなければ文字列のまま) */
const extValue = (ext: string | null) => {
  if (!ext) return null;
  const n = Number(ext);
  return Number.isFinite(n) ? n : ext;
};

export default function BbCredentialsPage() {
  const user = useCurrentUser();
  const toast = useToast();
  const [operators, setOperators] = useState<Operator[]>([]);
  const [credentials, setCredentials] = useState<Map<string, BbCredential>>(new Map());
  const [loading, setLoading] = useState(true);
  const [statuses, setStatuses] = useState<Set<OperatorStatus>>(() => new Set<OperatorStatus>(['active']));
  const [workStyles, setWorkStyles] = useState<Set<WorkStyle>>(() => new Set());
  const [payTypes, setPayTypes] = useState<Set<PayType>>(() => new Set());
  const [keyword, setKeyword] = useState('');
  const { sort, toggle } = useSort<SortKey>({ key: 'bb_id', dir: 'asc' });
  const [shown, setShown] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<Row | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [ops, creds] = await Promise.all([fetchOperators(), fetchBbCredentials()]);
      setOperators(ops);
      setCredentials(creds);
    } catch (e) {
      toast(e instanceof Error ? e.message : '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const rows: Row[] = useMemo(() => {
    // 並べ替え解除時も BB ID 順に戻るよう、基本順は常に BB ID 昇順
    const base = operators
      .filter((o) => matchesFilter(statuses, o.status))
      .flatMap((o) => o.bb_accounts.map((a) => ({ operator: o, account: a, credential: credentials.get(a.bb_id) ?? null })))
      .filter((r) => matchesFilter(workStyles, r.account.work_style ?? r.operator.work_style))
      .filter((r) => matchesFilter(payTypes, r.account.pay_type ?? r.operator.pay_type))
      .filter((r) => matchKeyword(keyword, r.operator.name, r.account.bb_id))
      .sort((a, b) => a.account.bb_id.localeCompare(b.account.bb_id));
    return sortRows(base, sort, (r, key) => {
      switch (key) {
        case 'bb_id':
          return r.account.bb_id;
        case 'ext':
          return extValue(r.account.ext);
        case 'name':
          return r.operator.name;
        case 'work_style':
          return WORK_STYLE_LABELS[r.account.work_style ?? r.operator.work_style] ?? null;
        case 'pay_type':
          return PAY_TYPE_LABELS[r.account.pay_type ?? r.operator.pay_type] ?? null;
        case 'business':
          return BUSINESS_LABELS[r.account.business];
        case 'status':
          return OPERATOR_STATUSES.indexOf(r.operator.status);
        case 'updated_at':
          return r.credential ? tsMillis(r.credential.updated_at) : null;
      }
    });
  }, [operators, credentials, statuses, workStyles, payTypes, keyword, sort]);

  const toggleShown = (bbId: string) =>
    setShown((prev) => {
      const next = new Set(prev);
      if (next.has(bbId)) next.delete(bbId);
      else next.add(bbId);
      return next;
    });

  return (
    <div className="screen master-screen" style={{ maxWidth: 1200 }}>
      <div className="card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          BB ID／パスワード一覧
          <span className="hint">{rows.length}件</span>
          <button className="txn-link" style={{ marginLeft: 'auto' }} onClick={() => setShown(shown.size > 0 ? new Set() : new Set(rows.map((r) => r.account.bb_id)))}>
            {shown.size > 0 ? 'すべて伏せる' : 'すべて表示'}
          </button>
        </h3>
        <p className="hint" style={{ marginBottom: 12 }}>管理者のみ閲覧できます。BlueBean側のアカウント登録は手作業です(API連携は未定)。</p>

        <div className="toolbar" style={{ marginBottom: 12 }}>
          <div className="tool-filter">
            <MultiSelect label="在籍" options={STATUS_OPTIONS} value={statuses} onChange={setStatuses} />
            <SearchBox value={keyword} onChange={setKeyword} placeholder="名前・BB IDで検索" />
            <MultiSelect label="出勤形態" options={WORK_STYLE_OPTIONS} value={workStyles} onChange={setWorkStyles} />
            <MultiSelect label="報酬区分" options={PAY_TYPE_OPTIONS} value={payTypes} onChange={setPayTypes} />
          </div>
        </div>

        {loading ? (
          <div className="empty">読み込み中…</div>
        ) : rows.length === 0 ? (
          <div className="empty">該当するBB IDがありません</div>
        ) : (
          <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <SortTh label="BB ID" sortKey="bb_id" sort={sort} onSort={toggle} defaultDir="asc" />
                <SortTh label="内線" sortKey="ext" sort={sort} onSort={toggle} defaultDir="asc" />
                <SortTh label="スタッフ名" sortKey="name" sort={sort} onSort={toggle} defaultDir="asc" />
                <SortTh label="出勤形態" sortKey="work_style" sort={sort} onSort={toggle} defaultDir="asc" />
                <SortTh label="報酬区分" sortKey="pay_type" sort={sort} onSort={toggle} defaultDir="asc" />
                <SortTh label="架電内容" sortKey="business" sort={sort} onSort={toggle} defaultDir="asc" />
                <SortTh label="在籍" sortKey="status" sort={sort} onSort={toggle} defaultDir="asc" />
                <th>パスワード</th>
                <SortTh label="更新" sortKey="updated_at" sort={sort} onSort={toggle} />
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const visible = shown.has(r.account.bb_id);
                return (
                  <tr key={r.account.bb_id} style={r.account.active ? undefined : { color: 'var(--muted)' }}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <b>{r.account.bb_id}</b>
                      {!r.account.active && <span className="hint">(無効)</span>}
                    </td>
                    <td>{r.account.ext ?? '—'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{r.operator.name}</td>
                    <td>{WORK_STYLE_LABELS[r.account.work_style] ?? '—'}</td>
                    <td>{PAY_TYPE_LABELS[r.account.pay_type] ?? '—'}</td>
                    <td>{BUSINESS_LABELS[r.account.business]}</td>
                    <td>
                      <span className={`badge ${r.operator.status === 'active' ? 'b-ok' : 'b-ng'}`}>{OPERATOR_STATUS_LABELS[r.operator.status]}</span>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {r.credential ? (
                        <>
                          <code style={{ fontFamily: 'ui-monospace, monospace', letterSpacing: visible ? 0 : 2 }}>
                            {visible ? r.credential.password : '••••••••'}
                          </code>
                          <button className="txn-link" onClick={() => toggleShown(r.account.bb_id)}>
                            {visible ? '伏せる' : '表示'}
                          </button>
                        </>
                      ) : (
                        <span className="hint">未登録</span>
                      )}
                    </td>
                    <td className="hint" style={{ whiteSpace: 'nowrap' }}>
                      {r.credential ? fmtTs(r.credential.updated_at, false) : '—'}
                    </td>
                    <td>
                      <button className="txn-link" onClick={() => setEditing(r)}>
                        {r.credential ? '変更' : '登録'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {editing && (
        <CredentialModal
          row={editing}
          userId={user.id}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void reload();
          }}
        />
      )}
    </div>
  );
}

function CredentialModal({ row, userId, onClose, onSaved }: { row: Row; userId: string; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [password, setPassword] = useState(row.credential?.password ?? '');
  const [note, setNote] = useState(row.credential?.note ?? '');
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await saveBbCredential({ bb_id: row.account.bb_id, op_id: row.operator.id, password, note }, userId);
      toast('保存しました');
      onSaved();
    } catch (err) {
      toast(err instanceof Error ? err.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    setBusy(true);
    try {
      await deleteBbCredential(row.account.bb_id, userId);
      toast('削除しました');
      onSaved();
    } catch (err) {
      toast(err instanceof Error ? err.message : '削除に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} width="min(480px, 94vw)">
      <h3>
        {row.account.bb_id} のパスワード
        <span className="hint" style={{ fontWeight: 400, marginLeft: 8 }}>
          {row.operator.name}
        </span>
      </h3>
      <form onSubmit={handleSubmit}>
        <div className="form-grid" style={{ marginTop: 14 }}>
          <div className="fw">
            <label>パスワード *</label>
            <input type="text" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="off" autoFocus />
          </div>
          <div className="fw">
            <label>メモ(任意)</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="例) 初期パスワード・変更予定など" />
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn-green" type="submit" disabled={busy}>
            保存する
          </button>
          <button className="btn-line" type="button" onClick={onClose}>
            キャンセル
          </button>
          {row.credential && (
            <button className="btn-line danger" type="button" disabled={busy} onClick={() => void handleDelete()}>
              削除
            </button>
          )}
        </div>
      </form>
    </Modal>
  );
}
